// ============================================================
// TUTORIAL CONFIGURATION
// Generated by Learn by Building Tutorial Generator
// Date: 2026-02-03
// Source: Video recording - "Import Contacts to a List"
// ============================================================
//
// NOTE: This tutorial uses the CHALLENGE-BASED writing style:
// - title: What to accomplish (not "click X")
// - instruction: Context and goal (not specific UI directions)
// - hint: Specific location guidance (shown only when user requests)
// ============================================================

// ------------------------------------------------------------
// TUTORIAL METADATA
// ------------------------------------------------------------
const tutorialConfig = {
  id: 'import-contacts-to-list',
  title: 'Import Contacts to a List',
  description: 'Learn how to create a new contact list in XM Directory and import contacts from a CSV file.',
  estimatedTime: '3 minutes',
  difficulty: 'Beginner',
  prerequisites: [],
  tags: ['xm-directory', 'contacts', 'lists', 'import', 'csv', 'directory'],
  brandColor: '#0066CC',
};

// ------------------------------------------------------------
// TUTORIAL STEPS
// ------------------------------------------------------------
const steps = [
  {
    id: 1,
    // Challenge-style: states WHAT to do, not HOW
    title: "Navigate to where contact lists are managed",
    // Context without giving away the exact button
    instruction: "From the XM Directory, we need to access the area where contact lists are organized.",
    screenshot: '/screenshots/import-contacts-to-list/step-1.png',
    hotspot: {
      top: 12,
      left: 16,
      width: 6,
      height: 4,
    },
    // Specific guidance saved for hint
    hint: 'Look for the "Lists" tab in the navigation bar near the top of the page, between "Directory contacts" and "Transactions".',
    wrongClickMessage: 'Not quite! Look for the "Lists" tab in the top navigation area.',
    requiresInput: false,
  },
  {
    id: 2,
    title: "Start creating a new contact list",
    instruction: "Now let's create a new list to organize our contacts for a specific distribution.",
    screenshot: '/screenshots/import-contacts-to-list/step-2.png',
    hotspot: {
      top: 26,
      left: 91,
      width: 9,
      height: 4,
    },
    hint: 'Look for the blue "Create a list" button in the top-right area of the Lists page.',
    wrongClickMessage: 'Not quite! Find the blue "Create a list" button on the right side of the page.',
    requiresInput: false,
  },
  {
    id: 3,
    title: "Name your new contact list",
    instruction: "Give your list a descriptive name that will help you identify which contacts it contains.",
    screenshot: '/screenshots/import-contacts-to-list/step-3.png',
    hotspot: {
      top: 51,
      left: 37,
      width: 18,
      height: 5,
    },
    hint: 'Click in the "List name" text field in the dialog and type a name for your list.',
    wrongClickMessage: 'Click in the List name input field to enter a name for your list.',
    requiresInput: true,
    inputPlaceholder: 'Q Bistro',
    inputValidation: /^.{1,}$/,
    inputField: {
      top: 51,
      left: 37,
      width: 18,
      height: 5,
    },
  },
  {
    id: 4,
    title: "Confirm the list creation",
    instruction: "Finalize creating your new contact list.",
    screenshot: '/screenshots/import-contacts-to-list/step-4.png',
    hotspot: {
      top: 67,
      left: 57,
      width: 8,
      height: 4,
    },
    hint: 'Click the blue "Confirm" button at the bottom of the dialog to create the list.',
    wrongClickMessage: 'Look for the blue "Confirm" button at the bottom right of the dialog.',
    requiresInput: false,
  },
  {
    id: 5,
    title: "Choose how to add contacts to your list",
    instruction: "Your list is empty and needs contacts. Select the method to add contacts from a file you've prepared.",
    screenshot: '/screenshots/import-contacts-to-list/step-5.png',
    hotspot: {
      top: 54,
      left: 39,
      width: 15,
      height: 18,
    },
    hint: 'Click on the "Upload a file" card to import contacts from a CSV or TSV file.',
    wrongClickMessage: 'Not quite! Look for the "Upload a file" option card in the center of the page.',
    requiresInput: false,
  },
  {
    id: 6,
    title: "Browse for your contacts file",
    instruction: "Select the file containing your contact information from your computer.",
    screenshot: '/screenshots/import-contacts-to-list/step-6.png',
    hotspot: {
      top: 37,
      left: 33,
      width: 10,
      height: 3,
    },
    hint: 'Click the blue "Select a file" link in the upload area to open your file browser.',
    wrongClickMessage: 'Look for the "Select a file" link in the center of the upload area.',
    requiresInput: false,
  },
  {
    id: 7,
    title: "Select your CSV file",
    instruction: "Navigate to the CSV file containing your contacts and select it.",
    screenshot: '/screenshots/import-contacts-to-list/step-7.png',
    hotspot: {
      top: 43,
      left: 18,
      width: 14,
      height: 3,
    },
    hint: 'Find and click on your CSV file in the file browser. Look in your Downloads folder or wherever you saved the file.',
    wrongClickMessage: 'Select the correct CSV file from the file list. Look for a file ending in .csv.',
    requiresInput: false,
  },
  {
    id: 8,
    title: "Confirm your file selection",
    instruction: "With your CSV file selected, confirm your choice to proceed with the upload.",
    screenshot: '/screenshots/import-contacts-to-list/step-8.png',
    hotspot: {
      top: 71,
      left: 83,
      width: 7,
      height: 4,
    },
    hint: 'Click the blue "Open" button in the bottom-right corner of the file picker.',
    wrongClickMessage: 'Click the "Open" button to confirm your file selection.',
    requiresInput: false,
  },
  {
    id: 9,
    title: "Proceed to the next step",
    instruction: "Your file has been validated. Continue to review and confirm the field mapping.",
    screenshot: '/screenshots/import-contacts-to-list/step-9.png',
    hotspot: {
      top: 95,
      left: 86,
      width: 8,
      height: 4,
    },
    hint: 'Click the blue "Next" button at the bottom right of the page to proceed.',
    wrongClickMessage: 'Look for the "Next" button at the bottom right corner of the page.',
    requiresInput: false,
  },
  {
    id: 10,
    title: "Complete the import",
    instruction: "Review the field mapping to ensure your contact data will be imported correctly, then finalize the upload.",
    screenshot: '/screenshots/import-contacts-to-list/step-10.png',
    hotspot: {
      top: 95,
      left: 81,
      width: 13,
      height: 4,
    },
    hint: 'Click the blue "Upload the file" button at the bottom right to complete the import process.',
    wrongClickMessage: 'Look for the "Upload the file" button at the bottom right of the page.',
    requiresInput: false,
  },
];

// ------------------------------------------------------------
// SCREENSHOT CHECKLIST
// ------------------------------------------------------------
/*
Screenshots needed for this tutorial:

□ step-1.png: XM Directory Summary page
  - Capture when: Initial view of XM Directory with Summary tab active
  - Key elements visible: Navigation tabs including "Lists", Summary content
  - Frame reference: frame_001.jpg (or similar showing full nav)

□ step-2.png: Lists page with existing lists
  - Capture when: After clicking Lists tab, page fully loaded
  - Key elements visible: "Create a list" button, list table with existing lists
  - Frame reference: frame_010.jpg

□ step-3.png: "Create a list" modal - empty
  - Capture when: After clicking "Create a list" button
  - Key elements visible: List name input field empty, Confirm button
  - Frame reference: frame_015.jpg

□ step-4.png: "Create a list" modal - with name entered
  - Capture when: After typing list name
  - Key elements visible: List name filled in (e.g., "Q Bistro"), Confirm button
  - Frame reference: frame_025.jpg

□ step-5.png: Empty list page with add contacts options
  - Capture when: After list is created, showing empty state
  - Key elements visible: "Upload a file" and "Manual input" cards
  - Frame reference: frame_030.jpg

□ step-6.png: Upload a file page - empty state
  - Capture when: After clicking "Upload a file"
  - Key elements visible: "Select a file" link, drag-drop area
  - Frame reference: frame_035.jpg

□ step-7.png: File picker dialog with CSV file visible
  - Capture when: File picker open, CSV file highlighted
  - Key elements visible: Downloads folder, CSV file selected
  - Frame reference: frame_040.jpg

□ step-8.png: File picker with file selected, Open button visible
  - Capture when: CSV file selected in picker
  - Key elements visible: Open button highlighted
  - Frame reference: frame_040.jpg (same frame, different hotspot)

□ step-9.png: File validated, ready to proceed
  - Capture when: After file uploaded, showing success message
  - Key elements visible: "Your file looks good!" message, Next button
  - Frame reference: frame_045.jpg

□ step-10.png: Field mapping confirmation page
  - Capture when: On "Confirm import fields" page
  - Key elements visible: Field mapping table, "Upload the file" button
  - Frame reference: frame_050.jpg
*/

// ------------------------------------------------------------
// EXPORTS
// ------------------------------------------------------------
export { tutorialConfig, steps };
